import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useCurrency } from "../lib/currency";
import ProductCard from "../components/ProductCard";
import VariantCard from "../components/VariantCard";
import CurrencyToggle from "../components/CurrencyToggle";
import { Button } from "../components/ui/button";
import { LogIn, Sparkles, Search, X, ChevronLeft } from "lucide-react";
import { Input } from "../components/ui/input";

export default function PublicStore() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryPhotos, setCategoryPhotos] = useState([]);
  const [activeCat, setActiveCat] = useState(null); // null = home (categories grid)
  const [activeSub, setActiveSub] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const { reload, heroImage, heroTitle, heroSubtitle } = useCurrency();

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [p, c, cp] = await Promise.all([
        api.get("/public/products"),
        api.get("/public/categories"),
        api.get("/public/category-photos").catch(() => ({ data: [] })),
      ]);
      setProducts(p.data || []);
      setCategories(c.data || []);
      setCategoryPhotos(cp.data || []);
      setLoading(false);
      reload();
    })();
  }, [reload]);

  const categoryPhotoByName = useMemo(() => {
    const m = {};
    (categoryPhotos || []).forEach((c) => { if (!c.parent) m[c.name] = c.photo_base64; });
    return m;
  }, [categoryPhotos]);

  const subcats = useMemo(() => {
    if (!activeCat) return [];
    return categories.find((c) => c.category === activeCat)?.subcategories || [];
  }, [categories, activeCat]);

  const filtered = useMemo(() => {
    if (!activeCat) return [];
    return products.filter((p) => {
      if (p.category !== activeCat) return false;
      if (activeSub && p.subcategory !== activeSub) return false;
      if (search && !`${p.name} ${p.category} ${p.subcategory}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [products, activeCat, activeSub, search]);

  // Flatten products → variants (in-stock only), preserving product order and variant order within product
  const filteredVariants = useMemo(() => {
    const rows = [];
    for (const p of filtered) {
      for (const v of p.variants || []) {
        if ((v.stock || 0) <= 0) continue;
        rows.push({ product: p, variant: v });
      }
    }
    return rows;
  }, [filtered]);

  const productCountByCat = useMemo(() => {
    const counts = {};
    for (const p of products) counts[p.category] = (counts[p.category] || 0) + 1;
    return counts;
  }, [products]);

  return (
    <div className="min-h-screen bg-background" data-testid="public-store">
      {/* Nav */}
      <header className="sticky top-0 z-40 glass-nav border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <span className="font-display text-2xl tracking-tight">Ladybeauty</span>
            <Sparkles className="w-4 h-4 text-[hsl(var(--accent))]" />
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <CurrencyToggle />
            <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex" data-testid="login-nav-btn">
              <Link to="/login"><LogIn className="w-4 h-4 mr-2" /> Área privada</Link>
            </Button>
            <Button asChild variant="ghost" size="icon" className="sm:hidden" data-testid="login-nav-btn-mobile">
              <Link to="/login"><LogIn className="w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-8">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          <div className="space-y-5 lb-fade-in">
            <div className="lb-overline">Beleza & Higiene · Cabo Verde</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              {heroTitle ? (
                heroTitle
              ) : (
                <>Cuidados que <em className="not-italic text-[hsl(var(--accent))]">brilham</em>,<br /> escolhidos com carinho.</>
              )}
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-lg">
              {heroSubtitle || "Produtos de higiene e beleza selecionados para elas e para eles. Encomendas rápidas via WhatsApp, entrega em Cabo Verde."}
            </p>
            <div className="flex items-center gap-3">
              <a
                href="#catalog"
                className="inline-flex items-center rounded-full bg-[hsl(var(--primary))] hover:bg-[#A21F58] text-white px-6 py-3 text-sm font-semibold transition-colors"
                data-testid="hero-explore-btn"
              >
                Explorar catálogo
              </a>
            </div>
          </div>
          <div className="relative aspect-[4/5] md:aspect-[5/6] rounded-3xl overflow-hidden lb-shadow-card">
            <img src={heroImage} alt="Ladybeauty" className="w-full h-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/40 to-transparent p-6">
              <div className="text-white font-display text-xl">Feminino & Masculino</div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section id="catalog" className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            {activeCat && (
              <button
                onClick={() => { setActiveCat(null); setActiveSub(""); setSearch(""); }}
                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                data-testid="back-to-categories"
              >
                <ChevronLeft className="w-4 h-4" /> Todas as categorias
              </button>
            )}
            <h2 className="font-display text-2xl sm:text-3xl">{activeCat || "Explorar por categoria"}</h2>
          </div>
          {activeCat && (
            <div className="relative max-w-sm w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Pesquisar produtos..."
                className="pl-9 bg-white"
                data-testid="search-input"
              />
            </div>
          )}
        </div>

        {activeCat && subcats.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2 items-center">
            <span className="text-xs text-muted-foreground mr-1">Subcategoria:</span>
            {subcats.map((s) => (
              <button
                key={s}
                onClick={() => setActiveSub(activeSub === s ? "" : s)}
                className={`px-3 py-1 rounded-full text-xs border ${activeSub === s ? "bg-[hsl(var(--accent))] text-white border-transparent" : "bg-white border-border text-muted-foreground hover:text-foreground"}`}
                data-testid={`subcat-filter-${s}`}
              >
                {s}
              </button>
            ))}
            {activeSub && (
              <button onClick={() => setActiveSub("")} className="text-xs text-muted-foreground inline-flex items-center gap-1">
                <X className="w-3 h-3" /> limpar
              </button>
            )}
          </div>
        )}
      </section>

      {/* Category grid OR product grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-8">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-square bg-white rounded-2xl lb-shadow-card animate-pulse" />
            ))}
          </div>
        ) : !activeCat ? (
          // Categories home
          categories.length === 0 ? (
            <div className="text-center py-20" data-testid="empty-state">
              <div className="font-display text-2xl mb-2">Sem produtos disponíveis</div>
              <p className="text-muted-foreground">Volta em breve — o catálogo é atualizado com frequência.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-8">
              {categories.map((c) => {
                const photo = categoryPhotoByName[c.category];
                return (
                  <button
                    key={c.category}
                    onClick={() => setActiveCat(c.category)}
                    className="group text-left bg-white rounded-2xl overflow-hidden lb-shadow-card lb-hover-lift lb-fade-in"
                    data-testid={`category-tile-${c.category}`}
                  >
                    <div className="aspect-square bg-[hsl(var(--secondary))] relative overflow-hidden">
                      {photo ? (
                        <img src={photo} alt={c.category} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="font-display text-4xl text-[hsl(var(--primary))]/30">{c.category?.slice(0, 1)}</span>
                        </div>
                      )}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                        <div className="text-white font-display text-2xl">{c.category}</div>
                        <div className="text-white/80 text-xs">{productCountByCat[c.category] || 0} produto(s)</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )
        ) : filtered.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-products-cat">
            <div className="font-display text-2xl mb-2">Nada em stock nesta categoria</div>
            <p className="text-muted-foreground">Tenta outra categoria ou volta em breve.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-8">
            {filteredVariants.map(({ product, variant }) => (
              <VariantCard key={`${product.id}-${variant.id}`} product={product} variant={variant} />
            ))}
          </div>
        )}
      </section>

      <footer className="border-t border-border mt-10 py-8 text-center text-sm text-muted-foreground">
        <div>© {new Date().getFullYear()} Ladybeauty · Cabo Verde</div>
      </footer>
    </div>
  );
}
